import React, { useState, useCallback, useEffect } from "react";
import { getMaterialIcon } from "#utils/icons";

const CloseIcon = getMaterialIcon("Close");
const SearchIcon = getMaterialIcon("Search");

/**
 * ForumSearch Component
 * Renders a search input field for forums.
 * Manages input state locally and calls parent functions to update/clear the search query.
 * @param {object} forum - The forum object (used primarily to reset state when it changes).
 * @param {function} setSearchQuery - Function called (debounced) when the input value changes.
 * @param {function} onClearQuery - Function called when the clear button is clicked or the forum changes.
 */
const ForumSearch = ({ setSearchQuery, onClearQuery, forum }) => {
    const [query, setQuery] = useState("");

    // Reset internal query state and call parent clear function when the forum context changes
    useEffect(() => {
        setQuery("");
        onClearQuery();
    }, [forum?.id, onClearQuery]);

    // Update local query state on input change
    const handleQueryChange = useCallback((newQuery) => {
        setQuery(newQuery);
        // Debounced call to parent setSearchQuery happens in useEffect below
    }, []);


    // Clear local query state and call parent clear function
    const handleClear = useCallback(() => {
        setQuery("");
        onClearQuery();
    }, [onClearQuery]);

    // Debounce the call to the parent setSearchQuery function
    useEffect(() => {
        const timer = setTimeout(() => {
            // Pass the trimmed query up to the parent component
            setSearchQuery(query.trim());
        }, 500); // 500ms debounce: waits 500ms after user stops typing

        return () => clearTimeout(timer); // Cleanup timer on unmount or if query/setSearchQuery changes
    }, [query, setSearchQuery]);

    return (
        <div className="relative w-full">
            <div className="relative">
                <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-700 w-5 h-5" />
                <input
                    type="text"
                    placeholder="Search forum..."
                    value={query}
                    onChange={(e) => handleQueryChange(e.target.value)} // Use internal handler
                    onKeyDown={(e) => { // Prevent Enter key submission behavior
                        if (e.key === 'Enter') {
                            e.preventDefault();
                        }
                    }}
                    className="w-full py-3 pl-10 pr-10 rounded-full border border-offBlack"
                />
                {query && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-700"
                    >
                        <CloseIcon className="w-5 h-5" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default ForumSearch; 